/**
 * This file is a fix for uPlot's `numAxisSplits` function.
 * In certain circumstances the function gets stuck in an infinite loop.
 * The fix here is to add a guard to the loop to prevent it from getting stuck.
 */

import type uPlot from 'uplot';

import {fixedDec, incrRoundUp, roundDec} from './util';

/**
 * Same contract as uPlot's `numAxisSplits` in
 * https://github.com/leeoniya/uPlot/blob/master/src/opts.js
 * (invoked as `axis.splits(self, i, min, max, _incr, _space, forceMin)` from uPlot.js).
 */
export function numAxisSplits(
  _self: uPlot,
  _axisIdx: number,
  scaleMin: number,
  scaleMax: number,
  foundIncr: number,
  _foundSpace: number,
  forceMin: boolean
) {
  const splits: number[] = [];

  const numDec = fixedDec.get(foundIncr) || 0;

  scaleMin = forceMin
    ? scaleMin
    : roundDec(incrRoundUp(scaleMin, foundIncr), numDec);

  // `val + foundIncr` can round to `val` (e.g. 1 + 1e-16 === 1); without a guard the loop never advances.
  let val = scaleMin;
  while (val <= scaleMax) {
    splits.push(Object.is(val, -0) ? 0 : val); // coalesces -0
    const next = roundDec(val + foundIncr, numDec);
    const stuck =
      foundIncr === 0 || (foundIncr > 0 ? next <= val : next >= val);
    if (stuck) {
      break;
    }
    val = next;
  }

  return splits;
}

/** @see numAxisSplits — packaged uPlot typings omit the 7th `forceMin` argument. */
export const numAxisSplitsForUPlotAxis = numAxisSplits as uPlot.Axis.Splits;
